import React, { forwardRef, useRef, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconComponent from "@CubeComponents/icons";

// Hardcoded sizing constants (pixel-perfect from Figma)
const INPUT_HEIGHT = 32;
const BORDER_RADIUS = 4;
const FONT_SIZE = 14;
const PADDING_Y = 4;
const PADDING_X = 8;
const HELPER_FONT_SIZE = 12;
const GAP = 8;

// Colors resolved from the design tokens (--Blue-450 / --Grey-500 etc.)
const COLORS = {
  accent: "#192048", // Blue-450 (Light Mode)
  muted: "#666A84", // Grey-500 (Light Mode)
  hoverBg: "#F1F4F9", // Grey-100 (Light Mode)
  error: "#E5484D",
  confirmation: "#30A46C",
  transparent: "#FFFFFF00",
};

const InfoIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14ZM8 5C8.41421 5 8.75 5.33579 8.75 5.75V8.75C8.75 9.16421 8.41421 9.5 8 9.5C7.58579 9.5 7.25 9.16421 7.25 8.75V5.75C7.25 5.33579 7.58579 5 8 5ZM8 11.5C8.41421 11.5 8.75 11.1642 8.75 10.75C8.75 10.3358 8.41421 10 8 10C7.58579 10 7.25 10.3358 7.25 10.75C7.25 11.1642 7.58579 11.5 8 11.5Z"
      fill={color}
    />
  </svg>
);

const HelperInfoIcon: React.FC<{ size?: number; color?: string }> = ({ size = 12, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="6" cy="6" r="5.5" stroke={color} strokeWidth="1" fill="none" />
    <path d="M6 3.5V6.5" stroke={color} strokeWidth="1" strokeLinecap="round" />
    <circle cx="6" cy="8.25" r="0.5" fill={color} />
  </svg>
);

export type NumericBoxVariant = "default" | "error" | "confirmation" | "dataOnly";

export interface NumericBoxProps {
  /** The variant/state of the numeric box */
  variant?: NumericBoxVariant;
  /** Width of the numeric box */
  width?: string | number;
  /** Placeholder text */
  placeholder?: string;
  /** Current value */
  value?: number;
  /** Default value for uncontrolled mode */
  defaultValue?: number;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Helper text displayed below the input */
  helperText?: string;
  /** Whether to show the helper icon */
  showHelperIcon?: boolean;
  /** Whether to show the info icon on the right */
  showInfoIcon?: boolean;
  /** Minimum value */
  min?: number;
  /** Maximum value */
  max?: number;
  /** Step value */
  step?: number;
  /** Additional sx styling */
  sx?: Record<string, any>;
  /** Additional class name */
  className?: string;
  /** Change handler */
  onChange?: (event: { target: { value: number } }) => void;
  /** Focus handler */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** Blur handler */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** Name attribute */
  name?: string;
  /** ID attribute */
  id?: string;
}

interface HelperTextProps {
  text?: string;
  textColor: string;
  iconColor: string;
  showIcon: boolean;
}

const HelperText: React.FC<HelperTextProps> = ({ text, textColor, iconColor, showIcon }) => {
  if (!text) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "0.25rem",
        marginTop: "0.5rem",
        height: "1rem",
      }}
    >
      {showIcon && (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            color: iconColor,
            flexShrink: 0,
          }}
        >
          <HelperInfoIcon size={12} color={iconColor} />
        </span>
      )}
      <Typography
        sx={{
          fontFamily: "'Source Sans Pro', sans-serif",
          fontSize: `${HELPER_FONT_SIZE / 16}rem`,
          fontWeight: 600,
          lineHeight: 1.2,
          color: textColor,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};

const accentForVariant = (variant: NumericBoxVariant): string => {
  if (variant === "error") return COLORS.error;
  if (variant === "confirmation") return COLORS.confirmation;
  return COLORS.accent;
};

const NumericBoxComponent = forwardRef<HTMLInputElement, NumericBoxProps>((props, ref) => {
  const {
    variant = "default",
    width = "15rem",
    placeholder = "Enter Value",
    value,
    defaultValue = 0,
    disabled = false,
    helperText,
    showHelperIcon = false,
    showInfoIcon = false,
    min = -Infinity,
    max = Infinity,
    step = 1,
    className,
    onChange,
    onFocus,
    onBlur,
    name,
    id,
  } = props;

  const innerRef = useRef<HTMLInputElement>(null);
  const inputRef = (ref as React.RefObject<HTMLInputElement>) || innerRef;
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue);

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;
  const hasValue = currentValue !== undefined && currentValue !== null && currentValue !== 0;

  const handleMouseEnter = useCallback(() => {
    if (!disabled) setIsHovered(true);
  }, [disabled]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(event);
    },
    [onFocus],
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(event);
    },
    [onBlur],
  );

  const handleValueChange = useCallback(
    (newValue: number) => {
      const clampedValue = Math.max(min, Math.min(max, newValue));
      if (!isControlled) {
        setInternalValue(clampedValue);
      }
      onChange?.({ target: { value: clampedValue } });
    },
    [min, max, isControlled, onChange],
  );

  const handleIncrement = useCallback(() => {
    setIsFocused(true);
    handleValueChange(Number(currentValue) + step);
  }, [currentValue, step, handleValueChange]);

  const handleDecrement = useCallback(() => {
    setIsFocused(true);
    handleValueChange(Number(currentValue) - step);
  }, [currentValue, step, handleValueChange]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;
      const numValue = parseFloat(rawValue);
      if (rawValue === "" || isNaN(numValue)) {
        handleValueChange(0);
      } else {
        handleValueChange(numValue);
      }
    },
    [handleValueChange],
  );

  const increaseDisabled = disabled || currentValue >= max;
  const decreaseDisabled = disabled || currentValue <= min;

  const accent = accentForVariant(variant);

  const getBorderColor = () => {
    if (variant === "dataOnly") {
      if (disabled || (!isFocused && !isHovered)) return COLORS.transparent;
      return accent;
    }
    if (disabled) return COLORS.muted;
    if (isFocused || isHovered || hasValue) return accent;
    return COLORS.muted;
  };

  const getBackgroundColor = () => {
    if (variant === "dataOnly") return COLORS.transparent;
    if (!disabled && isHovered) return COLORS.hoverBg;
    return COLORS.transparent;
  };

  const getTextColor = () => {
    if (disabled) return COLORS.muted;
    if (isFocused || isHovered || hasValue) return accent;
    return COLORS.muted;
  };

  return (
    <Box sx={{ width }} className={className} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Box
        sx={{
          display: "flex",
          gap: `${GAP / 16}rem`,
          height: `${INPUT_HEIGHT / 16}rem`,
          alignItems: "center",
          width: "100%",
        }}
      >
        {/* Main input box with chevrons on right — matches Figma layout */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            height: `${INPUT_HEIGHT / 16}rem`,
            padding: `${PADDING_Y / 16}rem ${PADDING_X / 16}rem`,
            borderRadius: `${BORDER_RADIUS / 16}rem`,
            border: `1px solid ${getBorderColor()}`,
            backgroundColor: getBackgroundColor(),
            transition: "all 150ms ease-in-out",
            minHeight: "1px",
            minWidth: "1px",
            opacity: disabled ? 0.6 : 1,
          }}
        >
          {/* Text input — left-aligned */}
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={currentValue}
            placeholder={placeholder}
            disabled={disabled}
            name={name}
            id={id}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              textAlign: "start",
              fontFamily: "'Source Sans Pro', sans-serif",
              fontSize: `${FONT_SIZE / 16}rem`,
              fontWeight: 400,
              lineHeight: 1.2,
              color: getTextColor(),
              width: "100%",
              minWidth: 0,
            }}
          />

          {/* Icon buttons — horizontal layout, inside the border */}
          <Box
            sx={{
              display: "flex",
              gap: "0.25rem",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <button
              type="button"
              tabIndex={-1}
              disabled={decreaseDisabled}
              onClick={() => handleDecrement()}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                border: "none",
                background: "transparent",
                lineHeight: 0,
                cursor: decreaseDisabled ? "not-allowed" : "pointer",
              }}
            >
              <IconComponent
                name="accordionArrowDown"
                width="1rem"
                height="1rem"
                className={decreaseDisabled ? "opacity-40" : ""}
              />
            </button>
            <button
              type="button"
              tabIndex={-1}
              disabled={increaseDisabled}
              onClick={() => handleIncrement()}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                border: "none",
                background: "transparent",
                lineHeight: 0,
                cursor: increaseDisabled ? "not-allowed" : "pointer",
              }}
            >
              <IconComponent
                name="accordionArrowUp"
                width="1rem"
                height="1rem"
                className={increaseDisabled ? "opacity-40" : ""}
              />
            </button>
          </Box>
        </Box>

        {/* Info icon — outside the input border */}
        {showInfoIcon && (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              width: "1rem",
              height: "1rem",
              color: getTextColor(),
            }}
          >
            <InfoIcon size={16} color={getTextColor()} />
          </span>
        )}
      </Box>

      <HelperText text={helperText} textColor={getTextColor()} iconColor={getTextColor()} showIcon={showHelperIcon} />
    </Box>
  );
});

NumericBoxComponent.displayName = "NumericBoxComponent";

export default NumericBoxComponent;
